import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faVideo, faAward } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './HeroSection.css';

// Images (put these files in public/assets/img/hero/)
const HERO_IMAGE = '/assets/img/hero/hero-girl.jpg';
const STEM_BADGE = '/assets/img/hero/stem-accredited.png';

// Shown if the stats API is unavailable (keep these true and slightly below the real numbers)
const FALLBACK_STATS = { students: 300, classes: 7900 };

// 315 -> "300+", 7,911 -> "7,900+": always rounded DOWN, so the claim stays true
const roundDown = (value, step) => Math.floor(Number(value) / step) * step;
const formatPlus = (n) => `${n.toLocaleString('en-US')}+`;

const HeroSection = () => {
  const reduceMotion = useReducedMotion();
  const [stats, setStats] = useState(FALLBACK_STATS);

  // Live numbers from the same API the old "fun facts" section used
  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/class-stats`);
        if (!res.ok) return;
        const data = await res.json();
        const students = roundDown(data.studentsEnrolled, 50);
        const classes = roundDown(data.classesCompleted, 100);
        if (!cancelled && students > 0 && classes > 0) setStats({ students, classes });
      } catch (err) {
        // keep the fallback numbers
      }
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  // Small fade-up for each block; turned off for users who prefer less motion
  const fadeUp = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 18 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.55, delay },
        };

  const whatsappUrl = `https://api.whatsapp.com/send/?phone=${process.env.REACT_APP_MOBILE_NUMBER1}&text=${encodeURIComponent(
    "Hi! I'd like to know more about RoboMind classes for my child."
  )}&type=phone_number&app_absent=0`;

  return (
    <section className="rmhero">
      <div className="rmhero-inner">
        <div className="rmhero-content">
          <motion.div className="rmhero-tag" {...fadeUp(0)}>
            <span className="rmhero-dot" aria-hidden="true" />
            Online · Live 1-to-1 · Ages 6–17
          </motion.div>

          <motion.h1 className="rmhero-title" {...fadeUp(0.08)}>
            Your child doesn’t just use technology.{' '}
            <span className="rmhero-highlight">They build it.</span>
          </motion.h1>

          <motion.p className="rmhero-text" {...fadeUp(0.16)}>
            Private coding, AI and robotics classes with a real instructor, on a schedule that fits around
            school.
          </motion.p>

          <motion.div className="rmhero-cta" {...fadeUp(0.24)}>
            <Link to="/FreeTrial" className="rmhero-btn rmhero-btn-primary">
              Book a Free Trial Class
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="rmhero-btn rmhero-btn-ghost">
              <FontAwesomeIcon icon={faWhatsapp} className="rmhero-wa" />
              WhatsApp Us
            </a>
          </motion.div>

          <motion.div className="rmhero-stats" {...fadeUp(0.32)}>
            <div className="rmhero-stat">
              <strong>{formatPlus(stats.students)}</strong>
              <span>students in Lebanon &amp; abroad</span>
            </div>
            <div className="rmhero-stat">
              <strong>{formatPlus(stats.classes)}</strong>
              <span>live classes completed</span>
            </div>
            <img src={STEM_BADGE} alt="STEM.org Accredited Educational Experience" className="rmhero-badge" />
          </motion.div>
        </div>

        <motion.div
          className="rmhero-visual"
          {...(reduceMotion
            ? {}
            : { initial: { opacity: 0, scale: 0.96 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.6, delay: 0.15 } })}
        >
          <img
            src={HERO_IMAGE}
            alt="Smiling student holding a laptop showing code"
            className="rmhero-photo"
            fetchpriority="high"
          />
          <div className="rmhero-chip rmhero-chip-top">
            <FontAwesomeIcon icon={faVideo} className="rmhero-chip-blue" />
            Live on Zoom
          </div>
          <div className="rmhero-chip rmhero-chip-bottom">
            <FontAwesomeIcon icon={faAward} className="rmhero-chip-yellow" />
            Certificate on completion
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;